import { DatePicker } from '../../components/ui/date-picker';
import { diaOperacional } from '../../lib/dia-operacional';
// apps/web/src/pages/planejamento/gantt-page.tsx — Fase 03, Plano 01/07 (OPS-05, D-01).
import React, { useEffect, useRef, useState } from 'react';
import type { GanttSnapshotData, ConflitosSnapshotResponse, OsDetalheResponse } from '@pieltec/contracts';
import { AlertOctagon, CheckCircle2, AlertTriangle, ZoomIn, ZoomOut, RefreshCw } from 'lucide-react';
import { initGantt, type GanttAdapterApi } from '../../lib/gantt-adapter';
import { CentralConflitosPanel } from './central-conflitos-panel';
import { OsDetalhePanel } from './os-detalhe-panel';
import { apiMutate } from '../../lib/csrf-fetch';
import { ehSnapshotGantt, ehSnapshotConflitos } from '../../lib/gantt-payload';

function mensagemDeErro(valor: unknown): string | null {
  if (typeof valor !== 'object' || valor === null) return null;
  const resposta = valor as Record<string, unknown>;
  return typeof resposta.message === 'string' ? resposta.message : null;
}

function ehDetalheOs(valor: unknown): valor is OsDetalheResponse {
  if (typeof valor !== 'object' || valor === null) return false;
  const os = valor as Record<string, unknown>;
  return (
    typeof os.id === 'string' && typeof os.opId === 'string' && typeof os.etapaOrdem === 'number' &&
    typeof os.celulaId === 'string' && typeof os.status === 'string' && Array.isArray(os.predecessores) &&
    Array.isArray(os.recursoAlternativoIds) && typeof os.terceirizado === 'boolean' &&
    (typeof os.fornecedorExternoId === 'string' || os.fornecedorExternoId === null) &&
    (typeof os.regraBloqueioLiberacao === 'object' || os.regraBloqueioLiberacao === null)
  );
}

export const GanttPage: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const adapterRef = useRef<GanttAdapterApi | null>(null);

  const hoje = new Date();
  const [dataInicio, setDataInicio] = useState(diaOperacional(hoje));
  const trintaDias = new Date(hoje.getTime() + 30 * 24 * 60 * 60 * 1000);
  const [dataFim, setDataFim] = useState(diaOperacional(trintaDias));

  const [snapshot, setSnapshot] = useState<GanttSnapshotData | null>(null);
  const [conflitos, setConflitos] = useState<ConflitosSnapshotResponse | null>(null);
  const [painelConflitosAberto, setPainelConflitosAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [tarefaSelecionada, setTarefaSelecionada] = useState<string | null>(null);
  // WR-07: detalhe da OS selecionada, carregado sob demanda para o OsDetalhePanel.
  const [osDetalhe, setOsDetalhe] = useState<OsDetalheResponse | null>(null);

  const fecharDetalhe = () => {
    setTarefaSelecionada(null);
    setOsDetalhe(null);
  };

  const carregarDetalheOs = async (osId: string) => {
    try {
      const res = await fetch(`/api/v1/planejamento/os/${osId}`);
      if (!res.ok) {
        // Ex.: o clique foi numa OP (projeto), não numa OS — não há painel a exibir.
        setOsDetalhe(null);
        return;
      }
      const detalhes: unknown = await res.json();
      setOsDetalhe(ehDetalheOs(detalhes) ? detalhes : null);
    } catch {
      setOsDetalhe(null);
    }
  };

  const handleLiberarBloqueio = async (osId: string, justificativa: string) => {
    const res = await apiMutate(`/api/v1/planejamento/os/${osId}/liberar-bloqueio`, {
      method: 'POST',
      body: { justificativa },
    });
    if (!res.ok) {
      const json: unknown = await res.json().catch(() => null);
      throw new Error(mensagemDeErro(json) ?? 'Falha ao liberar bloqueio da OS');
    }
    const detalhes: unknown = await res.json();
    if (!ehDetalheOs(detalhes)) throw new Error('Resposta inválida ao liberar bloqueio da OS');
    setOsDetalhe(detalhes);
  };

  useEffect(() => {
    if (tarefaSelecionada) {
      void carregarDetalheOs(tarefaSelecionada);
    } else {
      setOsDetalhe(null);
    }
  }, [tarefaSelecionada]);

  const carregarSnapshot = async () => {
    setCarregando(true);
    setErro(null);
    try {
      const [resSnap, resConf] = await Promise.all([
        fetch(`/api/v1/planejamento/gantt/snapshot?inicio=${dataInicio}&fim=${dataFim}`),
        fetch(`/api/v1/planejamento/conflitos?dataInicio=${dataInicio}&dataFim=${dataFim}`),
      ]);

      if (!resSnap.ok) {
        throw new Error(`Erro ao carregar snapshot: HTTP ${resSnap.status}`);
      }
      const data: unknown = await resSnap.json();
      if (!ehSnapshotGantt(data)) throw new Error('Snapshot de Gantt inválido.');
      setSnapshot(data);

      if (resConf.ok) {
        const confData: unknown = await resConf.json();
        setConflitos(ehSnapshotConflitos(confData) ? confData : null);
      }

      if (containerRef.current) {
        if (adapterRef.current) {
          adapterRef.current.loadData({
            data: [...data.data],
            links: [...data.links],
          });
        } else {
          adapterRef.current = initGantt(
            containerRef.current,
            { data: [...data.data], links: [...data.links] },
            (id) => setTarefaSelecionada(id),
          );
        }
      }
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha na comunicação com o servidor');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    void carregarSnapshot();
    return () => {
      if (adapterRef.current) {
        adapterRef.current.destroy();
        adapterRef.current = null;
      }
    };
  }, [dataInicio, dataFim]);

  return (
    <div className="flex flex-col min-w-0 w-full h-full space-y-4 p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            Planejamento de Produção & Gantt
          </h1>
          <p className="text-sm text-slate-500">
            Planeje a capacidade e identifique conflitos antes de liberar a produção.
          </p>
        </div>

        {/* Controles de Período e Ações */}
        <div className="gantt-toolbar min-w-0 max-w-full flex flex-wrap items-center gap-2">
          <div className="gantt-period">

            <DatePicker
              aria-label="Início do período" value={dataInicio}
              onValueChange={(value) => setDataInicio(value)}
              className="bg-transparent border-none focus:outline-none text-slate-800"
            />
            <span>até</span>
            <DatePicker
              aria-label="Fim do período" value={dataFim}
              onValueChange={(value) => setDataFim(value)}
              className="bg-transparent border-none focus:outline-none text-slate-800"
            />
          </div>

          <button
            onClick={() => adapterRef.current?.zoomIn()}
            className="p-2 border rounded-md hover:bg-slate-50 text-slate-700"
            title="Aproximar Zoom"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={() => adapterRef.current?.zoomOut()}
            className="p-2 border rounded-md hover:bg-slate-50 text-slate-700"
            title="Afastar Zoom"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <button
            onClick={() => setPainelConflitosAberto(true)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border transition-colors ${
              conflitos && conflitos.total > 0
                ? 'bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100'
                : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <AlertOctagon className={`w-3.5 h-3.5 ${conflitos && conflitos.total > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
            <span>Ver conflitos ({conflitos?.total ?? 0})</span>
          </button>

          <button
            onClick={() => void carregarSnapshot()}
            disabled={carregando}
            className="flex items-center gap-1 px-3 py-2 bg-primary text-primary-foreground rounded-md text-xs font-semibold hover:bg-primary-hover disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${carregando ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Alerta de Erro */}
      {erro && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 p-3 rounded-md flex items-center gap-2 text-sm">
          <AlertOctagon className="w-5 h-5 text-rose-600 flex-shrink-0" />
          <span>{erro}</span>
        </div>
      )}

      {/* Painel de Conflitos Detectados */}
      {snapshot && snapshot.conflitos.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2 text-amber-900 font-semibold text-sm">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span>{snapshot.conflitos.length} Conflito(s) de Planejamento Detectado(s)</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            {snapshot.conflitos.map((c) => (
              <div key={c.id} className="bg-white border border-amber-200 rounded p-2 flex items-start gap-2 shadow-sm">
                <AlertOctagon className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold text-rose-700 uppercase tracking-wider text-[10px] mr-1">
                    [{c.tipo}]
                  </span>
                  <span className="text-slate-800">{c.mensagem}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gauges de Capacidade das Células (03-UI-SPEC §A/Color) */}
      {snapshot && snapshot.celulas.length > 0 && (
        <div className="border rounded-lg p-3 bg-slate-50 space-y-2">
          <h2 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
            Ocupação & Disponibilidade por Célula
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {snapshot.celulas.map((c) => {
              const cap = c.capacidade;
              let corBarra = 'bg-emerald-500';
              let corTexto = 'text-emerald-700 bg-emerald-50 border-emerald-200';
              let icone = <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />;

              if (cap.estado === 'sobrecarregada') {
                corBarra = 'bg-rose-500';
                corTexto = 'text-rose-700 bg-rose-50 border-rose-200';
                icone = <AlertOctagon className="w-3.5 h-3.5 text-rose-600" />;
              } else if (cap.estado === 'proxima-limite') {
                corBarra = 'bg-amber-500';
                corTexto = 'text-amber-700 bg-amber-50 border-amber-200';
                icone = <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />;
              }

              const percentualClamped = Math.min(100, Math.max(0, cap.percentualOcupacao));

              return (
                <div key={c.codigo} className="bg-white border rounded-md p-2.5 shadow-sm space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-800 text-xs truncate" title={c.nome}>
                      {c.nome} ({c.codigo})
                    </span>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border flex items-center gap-1 ${corTexto}`}>
                      {icone}
                      {cap.estado}
                    </span>
                  </div>

                  {/* Barra de Progresso */}
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${corBarra}`}
                      style={{ width: `${percentualClamped}%` }}
                    />
                  </div>

                  {/* Rótulo numérico sempre visível em mono */}
                  <div className="flex justify-between items-center text-[11px] font-mono text-slate-600">
                    <span>{cap.horasPlanejadas.toFixed(1)}h planejadas</span>
                    <span className="font-bold">{cap.horasDisponiveis.toFixed(1)}h disponíveis ({cap.percentualOcupacao.toFixed(0)}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!carregando && snapshot && !snapshot.data.some(task => task.type !== 'project') && <div role="status" className="rounded-xl border border-dashed p-5 text-sm text-muted-foreground"><strong className="block text-foreground mb-1">Nenhuma etapa planejada neste período.</strong>Confira o período e os calendários das células. As etapas aparecerão após a liberação e o planejamento das ordens.<a href="/producao/nova-op" className="ml-2 underline">Ver produção</a></div>}
      {/* Widget do Gantt */}
      <div className="flex-1 min-h-[450px] border rounded-lg overflow-hidden bg-white shadow-inner relative">
        <div ref={containerRef} className="w-full h-full min-h-[450px]" />
      </div>

      {/* Painel Lateral Central de Conflitos */}
      {painelConflitosAberto && (
        <CentralConflitosPanel
          conflitos={conflitos}
          loading={carregando}
          onClose={() => setPainelConflitosAberto(false)}
          onNavigateToOs={(osId) => {
            setTarefaSelecionada(osId);
          }}
          onRecarregar={() => carregarSnapshot()}
        />
      )}

      {/* Painel de Detalhe da OS (OPS-10) — WR-07 */}
      {osDetalhe && (
        <div className="fixed inset-y-0 right-0 z-40">
          <OsDetalhePanel
            os={osDetalhe}
            onClose={fecharDetalhe}
            onLiberarBloqueio={handleLiberarBloqueio}
          />
        </div>
      )}
    </div>
  );
};
